/**
 * APIs used in License Overview:
 * - GET /orgs/:orgId/license/dashboard.read (or /ents/:entId/orgs/:orgId/license/dashboard.read)
 * - GET /orgs/:orgId/license/service/status.read (or /ents/:entId/orgs/:orgId/license/service/status.read)
 * - GET /orgs/:orgId/license/history.read (or /ents/:entId/orgs/:orgId/license/history.read)
 * - POST /orgs/:orgId/license/add.create (or /ents/:entId/orgs/:orgId/license/add.create) - triggered via ImportCreditsModal
 */

import React, { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";

// material-ui
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularLoader from "components/CircularLoader";
import { FormattedMessage } from "react-intl";

// project import
import { SolidButton } from "components/buttons/SolidButton";
import { ImportOutlined } from "@ant-design/icons";

// Hooks
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// Components
import StatsBlock from "./components/StatsBlock";
import AlertBlock from "./components/AlertBlock";
import UsageForecastBlock from "./components/UsageForecastBlock";
import SubscriptionsStatusBlock from "./components/SubscriptionsStatusBlock";
import RecentTransactionsBlock from "./components/RecentTransactionsBlock";
import ImportCreditsModal from "./components/ImportCreditsModal";

import { useNavigate } from "react-router-dom";

const LicenseOverview = ({ onTabChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();

  const [isLoading, setIsLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [statusData, setServiceStatusData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [openImportModal, setOpenImportModal] = useState(false);

  const settingsUuid = useMemo(() => uuidv4(), []);
  const dashboardUuid = useMemo(() => uuidv4(), []);
  const statusUuid = useMemo(() => uuidv4(), []);
  const historyUuid = useMemo(() => uuidv4(), []);

  const getIds = () => ({
    realmId: auth.realm?.id || "",
    orgId: auth.orgs?.id || "",
  });

  const fetchGeneralSettings = () => {
    const { realmId, orgId } = getIds();
    const settingsKey = standalone
      ? "/orgs/:orgId/settings/general.read"
      : "/ents/:entId/orgs/:orgId/settings/general.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    return {
      apiKey: settingsKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: settingsUuid,
    };
  };

  const fetchLicenseDashboard = () => {
    const { realmId, orgId } = getIds();
    const dashboardKey = standalone
      ? "/orgs/:orgId/license/dashboard.read"
      : "/ents/:entId/orgs/:orgId/license/dashboard.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    return {
      apiKey: dashboardKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: dashboardUuid,
    };
  };

  const fetchLicenseServiceStatus = () => {
    const { realmId, orgId } = getIds();
    const statusKey = standalone
      ? "/orgs/:orgId/license/service/status.read"
      : "/ents/:entId/orgs/:orgId/license/service/status.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    return {
      apiKey: statusKey,
      restfulPlaceholders: restfulParams,
      params: {},
      requestUuid: statusUuid,
    };
  };

  const fetchLicenseHistory = () => {
    const { realmId, orgId } = getIds();
    const historyKey = standalone
      ? "/orgs/:orgId/license/history.read"
      : "/ents/:entId/orgs/:orgId/license/history.read";
    const restfulParams = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };
    const now = dayjs().unix();
    const oneYearAgo = dayjs().subtract(1, "year").unix();

    return {
      apiKey: historyKey,
      restfulPlaceholders: restfulParams,
      params: {
        filter: {
          from: oneYearAgo,
          to: now,
          limit: 10,
        },
      },
      requestUuid: historyUuid,
    };
  };

  const init = async (silent = false) => {
    if (!silent) setIsLoading(true);

    try {
      const { getSendBatchData } = await sendBatch([
        fetchGeneralSettings(),
        fetchLicenseDashboard(),
        fetchLicenseServiceStatus(),
        fetchLicenseHistory(),
      ]);

      const settingsRes = getSendBatchData({ requestUuid: settingsUuid });
      const dashboardRes = getSendBatchData({ requestUuid: dashboardUuid });
      const statusRes = getSendBatchData({ requestUuid: statusUuid });
      const historyRes = getSendBatchData({ requestUuid: historyUuid });

      if (settingsRes?.data) setSettingsData(settingsRes.data);
      if (dashboardRes?.data) setDashboardData(dashboardRes.data);
      if (statusRes?.data) setServiceStatusData(statusRes.data);
      if (historyRes?.data) setHistoryData(historyRes.data);
    } catch (error) {
      console.error("[LicenseOverview] Error during initialization:", error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthReady) {
      init();
    }
  }, [isAuthReady, auth.orgs?.id, auth.realm?.id]);

  const handleOpenImportModal = () => setOpenImportModal(true);
  const handleCloseImportModal = () => setOpenImportModal(false);

  if (isLoading) return <CircularLoader />;
  if (!dashboardData || !statusData) {
    return (
      <Box
        sx={{
          height: "250px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.greyThird,
            fontSize: theme.typography.customFontSize[2],
          }}
        >
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          mb: 3,
          height: "64px",
          mt: -1, // Adjust to align with top of sidebar
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: "#172664",
            fontFamily: theme.typography.fontFamily,
            fontSize: "20px",
            fontWeight: "bold",
          }}
        >
          <FormattedMessage id='overview' />
        </Typography>
        <Stack
          direction='row'
          alignItems='center'
          spacing={3}
          sx={{ height: "100%" }}
        >
          <Divider
            orientation='vertical'
            sx={{
              height: "40px",
              borderColor: "rgba(0,0,0,0.08)",
              borderRightWidth: "1px",
            }}
          />
          <SolidButton
            onClick={handleOpenImportModal}
            width='auto'
            height='36px'
            startIcon={<ImportOutlined />}
            sx={{
              px: 3,
              borderRadius: "20px",
              bgcolor: "#172664",
              "&:hover": {
                bgcolor: "#23367d",
              },
            }}
          >
            Import Credits
          </SolidButton>
        </Stack>
      </Stack>
      <Grid container spacing={5}>
        <Grid item xs={12} sx={{ mb: -2 }}>
          <StatsBlock data={dashboardData} />
        </Grid>
        <Grid item xs={12}>
          <AlertBlock data={dashboardData} settingsData={settingsData} />
        </Grid>
        {dashboardData?.balance > 0 && (
          <Grid item xs={12}>
            <UsageForecastBlock
              settingsData={settingsData}
              dashboardData={dashboardData}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <SubscriptionsStatusBlock data={statusData} />
        </Grid>
        <Grid item xs={12}>
          <RecentTransactionsBlock
            transactions={historyData}
            walletName={standalone ? auth.realm?.name : auth.orgs?.name}
            onShowMore={() =>
              onTabChange
                ? onTabChange("transactions")
                : navigate("/license/transactions")
            }
          />
        </Grid>
      </Grid>

      <ImportCreditsModal
        auth={auth}
        openModal={openImportModal}
        handleCloseModal={handleCloseImportModal}
        onSaveChanges={init}
      />
    </Box>
  );
};

export default LicenseOverview;
